// Bhop block rule: landing and jumping again is fine any number of times. Staying on one block
// (standing or running) for more than BLOCK_TICKS ticks makes it drop.

import { BLOCK_TICKS, DT, HULL_HALF_WIDTH } from './constants';
import type { Box } from './collision/boxWorld';
import { hullHeight, type PlayerState } from './player';

export type BlockState = 'idle' | 'falling' | 'down';

const FALL_SPEED = 600;
const FALL_DEPTH = 400;
const DOWN_TIME = 2.5;

/**
 * Quick fail (the default): a dropped block sinks a little for this many ticks, then it's gone
 * and the player is put back at the checkpoint at once, instead of falling all the way down.
 */
export const QUICK_FAIL_TICKS = 20;
const QUICK_FALL_SPEED = 300;

export class BhopBlock {
  state: BlockState = 'idle';
  /** Seconds left in the 'down' state. */
  t = 0;
  /** Current vertical offset from the resting position (<= 0). */
  offset = 0;
  readonly baseMin: number;
  readonly baseMax: number;

  constructor(readonly box: Box) {
    this.baseMin = box.min[2];
    this.baseMax = box.max[2];
  }

  /** Ticks since the block started falling. */
  private fallTicks = 0;

  /**
   * Advance the drop animation by one tick. A dropped block only comes back when the player isn't
   * inside it. `quick`: sink a little, then vanish (see QUICK_FAIL_TICKS); otherwise the prototype's
   * long fall.
   */
  update(p: PlayerState, quick = false) {
    if (this.state === 'falling') {
      this.fallTicks++;
      if (quick) {
        this.offset -= QUICK_FALL_SPEED * DT;
        if (this.fallTicks >= QUICK_FAIL_TICKS) this.offset = -FALL_DEPTH;
      } else this.offset = Math.max(-FALL_DEPTH, this.offset - FALL_SPEED * DT);
      if (this.offset <= -FALL_DEPTH) {
        this.state = 'down';
        this.t = DOWN_TIME;
      }
    } else if (this.state === 'down') {
      this.t -= DT;
      if (this.t <= 0) {
        const b = this.box;
        const pos = p.pos;
        const hit =
          pos[0] - HULL_HALF_WIDTH < b.max[0] &&
          pos[0] + HULL_HALF_WIDTH > b.min[0] &&
          pos[1] - HULL_HALF_WIDTH < b.max[1] &&
          pos[1] + HULL_HALF_WIDTH > b.min[1] &&
          pos[2] < this.baseMax &&
          pos[2] + hullHeight(p) > this.baseMin;
        if (!hit) {
          this.state = 'idle';
          this.offset = 0;
          this.fallTicks = 0;
        }
      }
    }
    this.box.min[2] = this.baseMin + this.offset;
    this.box.max[2] = this.baseMax + this.offset;
    this.onOffset?.(this.offset);
  }

  /** Called with the drop offset after every update and reset (worlds that move solids by offset). */
  onOffset?: (offset: number) => void;

  reset() {
    this.state = 'idle';
    this.offset = 0;
    this.t = 0;
    this.fallTicks = 0;
    this.box.min[2] = this.baseMin;
    this.box.max[2] = this.baseMax;
    this.onOffset?.(0);
  }
}

/** Tracks how long the player has stood on the same block. */
export class BlockContact {
  block: BhopBlock | null = null;
  ticks = 0;

  /** Call once per tick after movement with the block under the player (or null). */
  update(block: BhopBlock | null | undefined) {
    if (block) {
      if (this.block === block) this.ticks++;
      else {
        this.block = block;
        this.ticks = 1;
      }
      if (this.ticks > BLOCK_TICKS && block.state === 'idle') block.state = 'falling';
    } else this.clear();
  }

  clear() {
    this.block = null;
    this.ticks = 0;
  }
}
